import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Users, GraduationCap, Search, Filter, Navigation } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { getOrganizations } from '../services/api';

export const FindOrganisation = () => {
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [sortBy, setSortBy] = useState('recent');
  const [distanceFilter, setDistanceFilter] = useState('all');

  // User location for distance calculation
  const [userLocation, setUserLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationReady, setLocationReady] = useState(false);

  // Get user's location on mount
  useEffect(() => {
    getUserLocation();
  }, []);

  const getUserLocation = () => {
    setLocationLoading(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
          setLocationLoading(false);
          setLocationReady(true);
        },
        (error) => {
          console.log('Location access denied or unavailable');
          setLocationLoading(false);
          setLocationReady(true);
        },
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 600000 }
      );
    } else {
      setLocationLoading(false);
      setLocationReady(true);
    }
  };

  useEffect(() => {
    fetchOrganizations();
  }, [searchQuery, locationReady, sortBy]);

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      const params = { search: searchQuery };

      // Add user location for distance calculation if available
      if (userLocation) {
        params.user_lat = userLocation.lat;
        params.user_lng = userLocation.lng;
      }

      if (sortBy) {
        params.sort_by = sortBy;
      }

      const data = await getOrganizations(searchQuery, params);
      // Handle both direct array response and pagination object response
      const orgs = Array.isArray(data)
        ? data
        : (data.organizations || data.data || []);
      setOrganizations(orgs);
    } catch (error) {
      console.error('Error fetching organizations:', error);
    } finally {
      setLoading(false);
    }
  };

  // Client-side filtering
  const filteredOrganizations = organizations.filter(org => {
    // Distance filter
    if (distanceFilter !== 'all' && userLocation) {
      const maxDist = parseInt(distanceFilter);
      const orgDist = org.distance_km;
      if (orgDist === null || orgDist === undefined || orgDist > maxDist) return false;
    }

    // Type filter
    if (typeFilter !== 'all' && org.organization_type !== typeFilter && org.type !== typeFilter) return false;

    // Location filter
    if (locationFilter && !org.city?.toLowerCase().includes(locationFilter.toLowerCase())) return false;

    return true;
  });

  // Sort organizations
  const sortedOrganizations = [...filteredOrganizations].sort((a, b) => {
    if (sortBy === 'distance' && userLocation) {
      const distA = a.distance_km ?? Infinity;
      const distB = b.distance_km ?? Infinity;
      return distA - distB;
    }
    return 0;
  });

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-gray-900 via-blue-800 to-purple-800 bg-clip-text text-transparent mb-3">Find Organisations</h1>
          <p className="text-lg text-gray-600">Connect with schools and coaching institutes</p>
        </div>

        {/* Search & Filters */}
        <div className="bg-white p-6 rounded-xl shadow-sm mb-8 border border-gray-100">
          <div className="grid md:grid-cols-3 gap-4 mb-4">
            <div className="relative md:col-span-2">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Search by name, location, or type..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="org-search-input"
              />
            </div>

            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger data-testid="type-filter">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="school">School</SelectItem>
                <SelectItem value="coaching_institute">Coaching Institute</SelectItem>
                <SelectItem value="university">University</SelectItem>
                <SelectItem value="online_platform">Online Platform</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Location Status */}
          {!userLocation && !locationLoading && (
            <div className="flex items-center gap-2 text-sm text-amber-600 mb-4">
              <Navigation className="h-4 w-4" />
              <span>Enable location to see distances and sort by nearest</span>
              <Button variant="link" size="sm" onClick={getUserLocation} className="text-blue-600 p-0 h-auto">
                Enable
              </Button>
            </div>
          )}

          {/* Advanced Filters Toggle */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            className="w-fit"
            data-testid="advanced-filters-toggle"
          >
            <Filter className="h-4 w-4 mr-2" />
            {showAdvancedFilters ? 'Hide' : 'Show'} Advanced Filters
          </Button>

          {/* Advanced Filters */}
          {showAdvancedFilters && (
            <div className="mt-4 pt-4 border-t">
              <div className="grid md:grid-cols-4 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Location</label>
                  <Input
                    placeholder="Filter by city..."
                    value={locationFilter}
                    onChange={(e) => setLocationFilter(e.target.value)}
                    data-testid="location-filter-input"
                  />
                </div>

                {userLocation && (
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">Max Distance</label>
                    <Select value={distanceFilter} onValueChange={setDistanceFilter}>
                      <SelectTrigger data-testid="distance-filter">
                        <SelectValue placeholder="Any Distance" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Any Distance</SelectItem>
                        <SelectItem value="5">Within 5 km</SelectItem>
                        <SelectItem value="10">Within 10 km</SelectItem>
                        <SelectItem value="25">Within 25 km</SelectItem>
                        <SelectItem value="50">Within 50 km</SelectItem>
                        <SelectItem value="100">Within 100 km</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Sort By</label>
                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger data-testid="sort-by-select">
                      <SelectValue placeholder="Sort By" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="recent">Most Recent</SelectItem>
                      {userLocation && <SelectItem value="distance">Nearest First</SelectItem>}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Results Count */}
        <div className="mb-6">
          <p className="text-gray-600">
            Showing <span className="font-semibold">{sortedOrganizations.length}</span> organisations
          </p>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <p className="text-gray-600">Loading organisations...</p>
          </div>
        )}

        {/* Organisations Grid */}
        {!loading && sortedOrganizations.length > 0 && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sortedOrganizations.map((org) => (
              <div
                key={org.id}
                className="bg-white rounded-xl shadow-sm hover:shadow-xl transition-all border border-gray-100 overflow-hidden group cursor-pointer"
                onClick={() => navigate(`/organizations/${org.id}`)}
                data-testid={`org-card-${org.id}`}
              >
                {/* Organisation Image */}
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={org.image}
                    alt={org.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <Badge className="absolute top-4 right-4 bg-blue-600">
                    {org.type}
                  </Badge>
                </div>

                {/* Organisation Info */}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {org.name}
                  </h3>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center space-x-2 text-gray-600 text-sm">
                      <MapPin className="h-4 w-4 flex-shrink-0" />
                      <span>{org.location || org.city}</span>
                      {org.distance_display && (
                        <span className="text-blue-600 font-medium">• {org.distance_display} away</span>
                      )}
                    </div>
                    <div className="flex items-center space-x-2 text-gray-600 text-sm">
                      <Users className="h-4 w-4 flex-shrink-0" />
                      <span>{org.students} students • {org.tutors} tutors</span>
                    </div>
                  </div>

                  {/* Courses */}
                  {org.courses && org.courses.length > 0 && (
                    <div className="mb-4">
                      <p className="text-sm font-medium text-gray-700 mb-2">Courses Offered:</p>
                      <div className="flex flex-wrap gap-1">
                        {org.courses.slice(0, 3).map((course, index) => (
                          <Badge key={index} variant="secondary" className="text-xs">
                            {course}
                          </Badge>
                        ))}
                        {org.courses.length > 3 && (
                          <Badge variant="secondary" className="text-xs">
                            +{org.courses.length - 3} more
                          </Badge>
                        )}
                      </div>
                    </div>
                  )}

                  <Button
                    onClick={(e) => { e.stopPropagation(); navigate(`/organizations/${org.id}`); }}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    data-testid={`view-details-${org.id}`}
                  >
                    View Details
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* No Results */}
        {!loading && sortedOrganizations.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-600 text-lg">No organisations found matching your search.</p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => setSearchQuery('')}
            >
              Clear Search
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
